import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import CircularProgress from 'tio-ui/components/circular-progress';
import type { CircularProgressColor } from 'tio-ui/components/circular-progress';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import { ANNUAL_WINDOW_DAYS } from 'tio-employee/utils/pslf/annual-auto-submit';
import type { AnnualAutoSubmitStatus } from 'tio-employee/utils/pslf/annual-auto-submit';
import type { IntlService } from 'ember-intl';

const RING_COLORS: Record<string, CircularProgressColor> = {
  auto: 'violet',
  warning: 'amber',
  danger: 'red',
  quiet: 'emerald',
};

/** `new Date('2019-04-01')` is UTC midnight, which renders as the day before west of UTC. */
function parseLocalDate(value: string) {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!parts) return new Date(value);
  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
}

interface SourceRow {
  key: string;
  labelKey: string;
  value: string;
}

export interface AnnualCountdownCardSignature {
  Args: {
    /** Resolved by `annualAutoSubmitStatus`; the card assumes it is inside the window. */
    status: AnnualAutoSubmitStatus;
    /** Opens the settings dialog on its "Your details" section; omitted, the summary has no link. */
    onManageDetails?: () => void;
  };
  Element: HTMLDivElement;
}

/** Hero countdown for the annual re-certification window — ring, tier copy, and the single CTA. */
export default class AnnualCountdownCard extends Component<AnnualCountdownCardSignature> {
  @service declare intl: IntlService;

  windowDays = ANNUAL_WINDOW_DAYS;

  get status() {
    return this.args.status;
  }

  get isAuto() {
    return this.status.tier === 'auto';
  }

  get isUrgent() {
    return this.status.tier === 'danger';
  }

  get ringColor() {
    return RING_COLORS[this.status.tier] ?? 'amber';
  }

  get dueDateLabel() {
    const due = this.status.dueDate;
    if (!due) return '';
    return this.intl.formatDate(due, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  get ringLabel() {
    return this.intl.t('pslf.reimagined.annual_auto_submit.ring_label', {
      days: this.status.daysRemaining,
      date: this.dueDateLabel,
    });
  }

  get titleKey() {
    if (!this.isAuto) return 'pslf.reimagined.annual_auto_submit.manual_title';
    return this.status.approverKind === 'employerSigns'
      ? 'pslf.reimagined.annual_auto_submit.auto_title_approver'
      : 'pslf.reimagined.annual_auto_submit.auto_title';
  }

  get descriptionKey() {
    if (this.isAuto) {
      return this.status.approverKind === 'employerSigns'
        ? 'pslf.reimagined.annual_auto_submit.auto_description_approver'
        : 'pslf.reimagined.annual_auto_submit.auto_description';
    }
    return this.isUrgent
      ? 'pslf.reimagined.annual_auto_submit.manual_description_urgent'
      : 'pslf.reimagined.annual_auto_submit.manual_description';
  }

  get ctaKey() {
    if (this.isAuto) return 'pslf.reimagined.annual_auto_submit.cta_submit_manually';
    return this.isUrgent
      ? 'pslf.reimagined.annual_auto_submit.cta_submit_now'
      : 'pslf.reimagined.annual_auto_submit.cta_submit_new';
  }

  get addressLabel() {
    const address = this.status.source?.borrowerAddress;
    if (!address) return '';
    const street = [address.addressLine1, address.addressLine2].filter(Boolean).join(' ');
    const region = [address.city, address.state].filter(Boolean).join(', ');
    return [street, region, address.postalCode].filter(Boolean).join(' ').trim();
  }

  get hoursLabel() {
    const hours = this.status.source?.employmentHours;
    if (hours === null || hours === undefined) return '';
    return this.intl.t('pslf.reimagined.annual_auto_submit.source.hours_value', { hours });
  }

  get startDateLabel() {
    const start = this.status.source?.employmentStartDate;
    if (!start) return '';
    const parsed = parseLocalDate(start);
    if (Number.isNaN(parsed.getTime())) return '';
    return this.intl.formatDate(parsed, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  /** Only rows the API actually filled: a blank line here would read as a blank on the form. */
  get sourceRows(): SourceRow[] {
    const source = this.status.source;
    if (!source) return [];

    const rows: SourceRow[] = [
      {
        key: 'employer',
        labelKey: 'pslf.reimagined.annual_auto_submit.source.employer',
        value: source.employerName ?? '',
      },
      {
        key: 'hours',
        labelKey: 'pslf.reimagined.annual_auto_submit.source.hours',
        value: this.hoursLabel,
      },
      {
        key: 'start-date',
        labelKey: 'pslf.reimagined.annual_auto_submit.source.start_date',
        value: this.startDateLabel,
      },
      {
        key: 'name',
        labelKey: 'pslf.reimagined.annual_auto_submit.source.name',
        value: source.borrowerName ?? '',
      },
      {
        key: 'email',
        labelKey: 'pslf.reimagined.annual_auto_submit.source.email',
        value: source.borrowerEmail ?? '',
      },
      {
        key: 'phone',
        labelKey: 'pslf.reimagined.annual_auto_submit.source.phone',
        value: source.borrowerPhone ?? '',
      },
      {
        key: 'address',
        labelKey: 'pslf.reimagined.annual_auto_submit.source.address',
        value: this.addressLabel,
      },
    ];

    return rows.filter((row) => row.value !== '');
  }

  get hasSourceRows() {
    return this.sourceRows.length > 0;
  }

  <template>
    <Card
      @padding="lg"
      class="tio-col"
      data-test-annual-countdown-card
      data-test-annual-countdown-tier={{this.status.tier}}
      ...attributes
    >
      <Text @variant="muted" @size="xs" @weight="semibold" class="uppercase tracking-wider">
        {{t "pslf.reimagined.annual_auto_submit.eyebrow"}}
      </Text>

      <div class="tio-row-loose">
        <CircularProgress
          @value={{this.status.daysRemaining}}
          @max={{this.windowDays}}
          @color={{this.ringColor}}
          @size="lg"
          @animated={{true}}
          @label={{this.ringLabel}}
        >
          <div class="text-center">
            <div class="tio-text-3xl-bold" data-test-annual-countdown-days>
              {{this.status.daysRemaining}}
            </div>
            <div class="tio-text-xs-semibold tio-fg-muted uppercase tracking-wider">
              {{#if this.isAuto}}
                {{t "pslf.reimagined.annual_auto_submit.days_unit"}}
              {{else}}
                {{t "pslf.reimagined.annual_auto_submit.days_left_unit"}}
              {{/if}}
            </div>
          </div>
        </CircularProgress>

        <div class="tio-col-tight min-w-0">
          <Heading @level={{3}} @size="base" data-test-annual-countdown-title>
            {{t this.titleKey date=this.dueDateLabel}}
          </Heading>
          <Text @variant="muted" @size="sm">
            {{t this.descriptionKey date=this.dueDateLabel days=this.status.daysRemaining}}
          </Text>
        </div>
      </div>

      {{#if this.isAuto}}
        {{#if this.hasSourceRows}}
          {{! `pii` keeps the borrower's name, contact details and address out of session replay. }}
          <div
            class="app-status-panel app-status-panel--brand p-4 pii"
            data-test-annual-source-details
          >
            <Text @size="sm" @weight="semibold" class="mb-2">
              {{t "pslf.reimagined.annual_auto_submit.source.heading"}}
            </Text>
            <Text
              @variant="muted"
              @size="sm"
              @tag="p"
              class="mb-3"
              data-test-annual-source-accuracy-note
            >
              {{t "pslf.reimagined.annual_auto_submit.source.accuracy_note"}}
            </Text>
            <dl class="tio-col-extra-tight">
              {{#each this.sourceRows as |row|}}
                <div
                  class="sm:grid sm:grid-cols-3 sm:gap-4"
                  data-test-annual-source-row={{row.key}}
                >
                  <dt class="tio-text-sm-medium tio-fg-muted">
                    {{t row.labelKey}}
                  </dt>
                  <dd class="tio-text-sm-normal min-w-0 sm:col-span-2">
                    {{row.value}}
                  </dd>
                </div>
              {{/each}}
            </dl>
            {{#if @onManageDetails}}
              <Button
                class="mt-2"
                @appearance="link"
                @intent="primary"
                @size="sm"
                @testHook="pslf-annual-countdown__manage-details"
                data-test-annual-manage-details
                {{on "click" @onManageDetails}}
              >
                {{t "pslf.reimagined.annual_auto_submit.source.manage_link"}}
              </Button>
            {{/if}}
          </div>
        {{/if}}
        <Text @variant="muted" @size="xs" data-test-annual-manual-override-note>
          {{t "pslf.reimagined.annual_auto_submit.manual_override_note"}}
        </Text>
      {{/if}}

      <Button
        class="mt-auto self-start"
        @appearance={{if this.isAuto "outlined" "default"}}
        @intent={{if this.isUrgent "danger" "primary"}}
        @testHook="pslf-annual-countdown__submit"
        data-test-annual-countdown-cta
        {{on "click" (transitionTo "authenticated.pslf.dashboard.start-new")}}
      >
        {{t this.ctaKey}}
      </Button>
    </Card>
  </template>
}
